import { useCallback } from 'react';
import { isEmpty } from 'lodash';

import { chain } from '@lyearn/core/utils/immutable';
import { TagFilterTypeFragment } from '@/components/FilterManager/configs/tags/graphql/queries/generated/tagFilter';
import { isTagValueEmptyNew } from '@/components/FilterManager/SortAndFilter/components/TagsFilterContainer/utils';
import { FilterState } from '@/components/FilterManager/types';
import { NewTagValues } from '@/modules/corporate/modules/tags/types';
import { EntityTagInput, TagTypeEnum } from '@/types/schema';

export const valueFieldName: {
  [key in TagTypeEnum]: keyof EntityTagInput;
} = {
  [TagTypeEnum.Date]: 'dateValue',
  [TagTypeEnum.Number]: 'numberValue',
  [TagTypeEnum.Text]: 'textValue',
  [TagTypeEnum.MultiSelect]: 'optionIdValues',
  [TagTypeEnum.SingleSelect]: 'optionIdValues',
  [TagTypeEnum.User]: 'userIdValues',
};

export type UseTagStateAndMethodsType = ReturnType<typeof useTagStateAndMethods>;

export const useTagStateAndMethods = ({
  filter,
  setFilter,
  id,
}: {
  id: string;
  filter: FilterState;
  setFilter: (filter: FilterState) => void;
}) => {
  const selectedFilters = filter[id];

  const onTagTypeFilterChange = useCallback(
    (val: NewTagValues, selectedTag: TagFilterTypeFragment) => {
      const tagId = selectedTag.id;
      const updater = chain(filter);
      if (isTagValueEmptyNew(val, selectedTag)) {
        updater.updateIn(id, (tags: EntityTagInput[] = []) =>
          tags.filter((tag) => tag.tagId !== tagId),
        );
      } else {
        updater.updateIn(id, (tags: EntityTagInput[] = []) => {
          const targetTag = tags.filter((tag) => tag.tagId == tagId);

          if (isEmpty(targetTag)) {
            const newTag = {
              [valueFieldName[selectedTag.type]]: val,
              type: selectedTag.type,
              tagId: tagId,
            };
            tags.push(newTag);
            return tags;
          } else {
            return tags.map((tag) => {
              if (tag.tagId === tagId) {
                return { ...tag, [valueFieldName[selectedTag.type]]: val };
              }
              return tag;
            });
          }
        });
      }
      setFilter(updater.value());
    },
    [filter, id, setFilter],
  );

  const onRemoveTag = useCallback(
    (tagId: string) => {
      setFilter(
        chain(filter)
          .updateIn(id, (tags: EntityTagInput[]) => tags.filter((tag) => tag.tagId !== tagId))
          .value(),
      );
    },
    [filter, id, setFilter],
  );

  return {
    state: { selectedFilters },
    methods: {
      onTagTypeFilterChange,
      onRemoveTag,
    },
  };
};
